import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  AgentSummaryResponse,
  CreateAgentBody,
  UpdateAgentBody,
} from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

export type AgentStatusFilter = 'active' | 'deactivated' | 'all';

export interface AgentFilters {
  roleId: string | null;
  status: AgentStatusFilter;
  search: string;
}

export function useAgents(
  { enabled = true, pollInterval = 30_000 }: { enabled?: boolean; pollInterval?: number } = {},
) {
  const apiClient = useApiClient();
  const [agents, setAgents] = useState<AgentSummaryResponse[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [filters, setFiltersState] = useState<AgentFilters>({
    roleId: null,
    status: 'active',
    search: '',
  });
  const filtersRef = useRef(filters);
  const inFlightRef = useRef(false);
  const loadedRef = useRef(false);

  const fetch = useCallback(() => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const f = filtersRef.current;
    const query: { status?: AgentStatusFilter; role_id?: string } = {};
    query.status = f.status;
    if (f.roleId) query.role_id = f.roleId;
    apiClient
      .listAgents(query)
      .then((rows) => {
        setAgents(rows);
        loadedRef.current = true;
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        if (!loadedRef.current) setStatus('error');
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  }, [apiClient]);

  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;

  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    fetchRef.current();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchRef.current();
    }, pollInterval);
    return () => clearInterval(id);
  }, [enabled, pollInterval]);

  const setFilters = useCallback((f: AgentFilters) => {
    filtersRef.current = f;
    setFiltersState(f);
    if (loadedRef.current) {
      inFlightRef.current = false;
      fetchRef.current();
    }
  }, []);

  // Client-side search narrowing — the server already filters by status+role
  // but search is left as a client concern because list sizes are small.
  const filtered = useMemo(() => {
    const f = filters;
    let result = [...agents];
    if (f.search) {
      const q = f.search.toLowerCase();
      result = result.filter(
        (a) =>
          a.slug.toLowerCase().includes(q) ||
          a.name.toLowerCase().includes(q) ||
          (a.description ?? '').toLowerCase().includes(q),
      );
    }
    result.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return result;
  }, [agents, filters]);

  const roleCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const a of agents) {
      const slug = a.role?.slug ?? 'unknown';
      c[slug] = (c[slug] ?? 0) + 1;
    }
    return c;
  }, [agents]);

  const createAgent = useCallback(
    async (body: CreateAgentBody) => {
      const created = await apiClient.createAgent(body);
      // Optimistic insert; the detail object is shape-compatible with the
      // summary because AgentDetailResponse extends AgentSummaryResponse.
      setAgents((prev) => [created, ...prev]);
      return created;
    },
    [apiClient],
  );

  const updateAgent = useCallback(
    async (id: string, body: UpdateAgentBody) => {
      const updated = await apiClient.updateAgent(id, body);
      setAgents((prev) => prev.map((a) => (a.id === id ? updated : a)));
      return updated;
    },
    [apiClient],
  );

  const deactivateAgent = useCallback(
    async (id: string) => {
      const deactivated = await apiClient.deactivateAgent(id);
      // Status filter == 'active' (default) — drop from local list. Status
      // filter == 'all' or 'deactivated' — keep but with new status. Easiest
      // and correct: refetch.
      setAgents((prev) => prev.map((a) => (a.id === id ? deactivated : a)));
      if (filtersRef.current.status === 'active') {
        setAgents((prev) => prev.filter((a) => a.id !== id));
      }
      return deactivated;
    },
    [apiClient],
  );

  const scaffoldSoul = useCallback(
    async (id: string) => {
      const result = await apiClient.scaffoldAgentSoul(id);
      // Refresh just this row so has_soul_md flips on the card.
      const refreshed = await apiClient.getAgent(id);
      setAgents((prev) => prev.map((a) => (a.id === id ? refreshed : a)));
      return result;
    },
    [apiClient],
  );

  const reload = useCallback(() => {
    if (loadedRef.current) {
      inFlightRef.current = false;
      fetchRef.current();
    }
  }, []);

  return {
    agents: filtered,
    allAgents: agents,
    status,
    error,
    filters,
    setFilters,
    roleCounts,
    createAgent,
    updateAgent,
    deactivateAgent,
    scaffoldSoul,
    reload,
  };
}
